// Confirm modal for remove/delete flows.
//
// Two modes (per operator guidance 2026-07-21):
//   destructive=true  (default) — irreversible actions (hard deletes,
//     external-system teardown). Requires typing "delete" to enable confirm
//     (per feedback_delete_confirm_word.md).
//   destructive=false — reversible actions (soft-deletes, unassigns).
//     Plain yes/no confirm, no typing.

import { useState } from 'react';

export default function ConfirmDeleteDialog({ title, body, confirmLabel, onConfirm, onCancel, isPending, error, extra, destructive = true }) {
  const [typed, setTyped] = useState('');
  const matches = !destructive || typed.trim().toLowerCase() === 'delete';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <p className="mt-2 text-sm text-gray-600">{body}</p>
        {extra}
        {destructive && (
          <>
            <p className="mt-3 text-sm text-gray-600">
              Type <span className="font-mono font-semibold text-red-600">delete</span> to confirm:
            </p>
            <input
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="mt-2 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500"
              placeholder="delete"
              autoFocus
              aria-describedby={typed && !matches ? 'confirm-delete-mismatch' : undefined}
            />
            {/* SAY WHY THE BUTTON IS OFF. Kevin typed "deslete" on line 63 and reported that
                delete does not work — and from the screen he was right: the word was wrong,
                the button was greyed, and nothing on the dialog connected the two. A control
                disabled for a reason the user cannot see is indistinguishable from a broken
                one, which is the same fault the Save-and-Apply tooltip fixed. Shown only
                once there is something to correct, so an empty box is not scolded. */}
            {typed && !matches && (
              <p id="confirm-delete-mismatch" className="mt-1.5 text-xs text-amber-700">
                That doesn’t match. Type <span className="font-mono font-semibold">delete</span> exactly.
              </p>
            )}
          </>
        )}
        {error && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          {/* The title hangs on a WRAPPER, not the button: a disabled button takes no
              pointer events, so a title on it never appears — the same trap that made the
              Save-and-Apply tooltip invisible (docs/UI_GUIDELINES.md). */}
          <span title={matches ? undefined : 'Type “delete” in the box above to enable this.'}>
            <button
              onClick={onConfirm}
              disabled={!matches || isPending}
              className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:pointer-events-none disabled:opacity-50"
            >
              {isPending ? 'Working...' : (confirmLabel || 'Delete')}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
}
